import React, { useEffect, useState } from "react";

import { Grid } from "@mui/material";
import {
  bridgeLoanAdjustedRatioFields,
  bridgeLoanFinancedRatiosFields,
  bridgeLoanMaxGuidelineLeverageFields,
  bridgeLoanRequestedRatiosFields,
  getKeyDealMetricsFieldMap,
  investorDSCRActualRatiosFields,
  investorDscrMaxGuidelineLeverageFields,
  investorDscrNetLeverageFields,
  KeyMetricFieldType
} from "./configs/KeyMetricsConfig";
import { KeyDealMetricStyle } from "./KeyDealMetricStyle";
import { formatValueByType } from "../utils/formatChecks";
import { KeyMetricsTextField } from "./KeyMetricsTextField";
import { getTemplateData } from "../network/apiService";
import KeyMetricsTable from "./KeyMetricsTable";

export const KeyDealSectionMap = (props: any) => {
  const { data } = props;
  const classes = KeyDealMetricStyle();
  const [orgSpecificTestsLables, setOrgSpecificTestsLabels] = useState<any>({});
  const [rows, setRows] = useState<any[]>([]);

  const { loanStage } = data;

  const getRowValues = (data: any) => {
    const rowValues: any[] = [];
    const requestedValues: any = { label: "Original Ratios", };
    const financedValues: any = { label: "Financed Ratios", };
    const maxValues: any = { label: "Maximum Guideline Leverage", };
    const adjustedRatios: any = { label: "Adjusted Ratios", };
    const actualRatios: any = { label: "Actual Ratios", };
    const netLeverageRatios: any = { label: "Net Leverages", };
    const maxLeverages: any = { label: "Max Leverages", };

    if (data?.loanType === "BridgeLoan") {
      bridgeLoanRequestedRatiosFields?.forEach((field) => {
        const value = field?.getValue(data);
        requestedValues[field.fieldId] = formatValueByType(value, field?.fieldType);
      });
      bridgeLoanFinancedRatiosFields?.forEach((field) => {
        const value = field?.getValue(data);
        financedValues[field.fieldId] = formatValueByType(value, field?.fieldType);
      });
      bridgeLoanMaxGuidelineLeverageFields?.forEach((field) => {
        const value = field?.getValue(data);
        maxValues[field.fieldId] = formatValueByType(value, field?.fieldType);
      });
      bridgeLoanAdjustedRatioFields?.forEach((field: any) => {
        const value = field?.getValue(data);
        adjustedRatios[field.fieldId] = formatValueByType(value, field?.fieldType);
      });
      rowValues.push(requestedValues, financedValues, maxValues, adjustedRatios);
      setRows(rowValues);
    } else {
      investorDSCRActualRatiosFields?.forEach((field) => {
        const value = field?.getValue(data);
        actualRatios[field.id ?? ""] = formatValueByType(value, field?.fieldType);
      });
      investorDscrNetLeverageFields?.forEach((field) => {
        const value = field?.getValue(data);
        netLeverageRatios[field.id ?? ""] = formatValueByType(value, field?.fieldType);
      });
      investorDscrMaxGuidelineLeverageFields?.forEach((field) => {
        const value = field?.getValue(data);
        maxLeverages[field.id ?? ""] = formatValueByType(value, field?.fieldType);
      });
      rowValues.push(actualRatios, maxLeverages, netLeverageRatios);
      setRows(rowValues);
    }
  };

  useEffect(() => {
    const { ruleResult } = data ?? {};
    const { loanResult } = ruleResult ?? {};
    const loanId = Object.keys(loanResult ?? {})[0];
    const { orgSpecificTests } = loanResult?.[loanId] ?? {};
    const keys = Object.keys(orgSpecificTests || {});
    const templateKeys = keys.map((key: string) => `testNames.${key}`);
    if (data?.ruleResult) {
      getRowValues(data);
    }
    getTemplateData({ templateKeys })
      .then((resp: any) => {
        setOrgSpecificTestsLabels(resp?.data ?? {});
      })
      .catch(() => {
        setOrgSpecificTestsLabels({});
      });
  }, [data]);

  return (
    <Grid>
      {getKeyDealMetricsFieldMap({ ...data, orgSpecificTestsLables }).map(
        (ele) => {
          const { sectionLabel, sectionId, fields, type } = ele;
          if (ele?.isHidden && ele?.isHidden?.(data)) {
            return null;
          }
          return (
            <Grid key={sectionId}>
              <Grid
                xs={12}
                className={
                  sectionLabel
                    ? `${classes.sectionHeader} keydeal-section-header`
                    : ""
                }
                style={{ display: "flex" }}
              >
                <span
                  className={classes.sectionHeading}
                  style={{ minWidth: 130, maxWidth: 250 }}
                >
                  {sectionLabel}
                </span>
              </Grid>
              {sectionId === "propertyInfo" ? (
                <Grid xs={12} container style={{ paddingLeft: "13px" }}>
                  {data.propertyDetails?.map((property: any, idx: number) => {
                    return (
                      <Grid container sx={{ marginBottom: "8px" }} key={idx}>
                        {fields
                          .filter(
                            (fieldItem: any) =>
                              !fieldItem.showInStage ||
                              fieldItem.showInStage.includes(loanStage)
                          )
                          .map((item: KeyMetricFieldType) => {
                            const {
                              fieldId,
                              fieldLabel,
                              getValue,
                              fieldType,
                              popOverData,
                              redirectionType,
                              customValue,
                              isHidden
                            } = item;
                            const value = getValue(data, idx, false);
                            if (isHidden && isHidden?.(data)) return null;

                            return (
                              <Grid
                                item
                                // style={{ minWidth: 130, maxWidth: 250 }}
                                key={fieldId}
                              >
                                <KeyMetricsTextField
                                  label={fieldLabel}
                                  redirectionType={redirectionType}
                                  value={formatValueByType(value, fieldType)}
                                  key={`${fieldId}-field`}
                                  popOverData={
                                    popOverData
                                      ? popOverData(data, idx, false)
                                      : null
                                  }
                                  customValue={
                                    customValue ? customValue(data) : null
                                  }
                                />
                              </Grid>
                            );
                          })}
                      </Grid>
                    );
                  })}
                  {data.propertyDetails?.length > 1 ? (
                    <Grid container sx={{ marginBottom: "8px" }}>
                      {fields
                        .filter(
                          (fieldItem: any) =>
                            !fieldItem.showInStage ||
                            fieldItem.showInStage.includes(loanStage)
                        )
                        .map((item: KeyMetricFieldType) => {
                          const {
                            fieldId,
                            fieldLabel,
                            getValue,
                            fieldType,
                            popOverData,
                            redirectionType,
                            customValue,
                            isHidden
                          } = item;
                          const value = getValue(data, 0, true);
                          if (isHidden && isHidden?.(data)) return null;
                          return (
                            <Grid
                              item
                              // style={{ minWidth: 130, maxWidth: 250 }}
                              key={fieldId}
                            >
                              <KeyMetricsTextField
                                label={fieldLabel}
                                redirectionType={redirectionType}
                                value={formatValueByType(value, fieldType)}
                                key={`${fieldId}-field`}
                                popOverData={
                                  popOverData
                                    ? popOverData(data, 0, true)
                                    : null
                                }
                                customValue={
                                  customValue ? customValue(data) : null
                                }
                              />
                            </Grid>
                          );
                        })}
                    </Grid>
                  ) : null}
                </Grid>
              ) : (
                <Grid xs={12} container style={{ paddingLeft: "13px" }}>
                  {type === "table" ? (
                    <KeyMetricsTable tableConfig={fields} rows={rows}/>
                  ) : (
                    fields
                      .filter(
                        (fieldItem: any) =>
                          !fieldItem.showInStage ||
                          fieldItem.showInStage.includes(loanStage)
                      )
                      .map((item: KeyMetricFieldType) => {
                        const {
                          fieldId,
                          fieldLabel,
                          getValue,
                          fieldType,
                          popOverData,
                          redirectionType,
                          customValue,
                          isHidden
                        } = item;
                        const value = getValue?.(data, undefined, data?.propertyDetails?.length > 1);
                        if (isHidden && isHidden?.(data)) return null;
                        return (
                          <Grid
                            item
                            style={{ minWidth: 130, maxWidth: 250 }}
                            key={fieldId}
                          >
                            <KeyMetricsTextField
                              label={fieldLabel}
                              redirectionType={redirectionType}
                              value={formatValueByType(value, fieldType)}
                              key={`${fieldId}-field`}
                              popOverData={
                                popOverData ? popOverData(data) : null
                              }
                              customValue={
                                customValue ? customValue(data) : null
                              }
                            />
                          </Grid>
                        );
                      })
                  )}
                </Grid>
              )}
            </Grid>
          );
        }
      )}
    </Grid>
  );
};
